import { faTasks } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    Modal, ModalBody,
} from 'reactstrap';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    LinkCellRenderer,
    DateFilterDefaults,
    getServerSideRowRequest,
    EmployeeAccountStatusRenderer,
    TextFilterDefaults
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    FlexCenterRow, PageHeading, PageWrap, toasty,
} from '../common/forms/FormElements';
import { util } from '../Util';
import { JobDashboardDetailForm } from './JobDashboardDetailForm';

// Added by MFN.  May need to be removed/altered.
class JobDashboardIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.detailForm = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            showDetailForm: false,
            selectedData: {}
        };

        this.onRowSelected = this.onRowSelected.bind(this);
        this.onDetailFormClosed = this.onDetailFormClosed.bind(this);
    }

    componentDidMount() { return this.populateState(); }

    componentWillUnmount() { return this.setState = (state, callback) => { }; }

    onShowDetail = (id) => {
        this.detailForm.current.open(id);
    };

    // Before allowing the excel export, check specified date range, if any, and try to
    // protect against abnormally large datasets.  We don't use the way in DataGridToolbar
    // so we can launch a popup beforehand.
    onExportToExcelClicked = async () => {
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);

        const from = req?.filterModel['Timesheet.JobDate']?.dateFrom;
        const to = req?.filterModel['Timesheet.JobDate']?.dateTo;

        if (!from || !to) {
            toasty.error('Please specify a date range when exporting Charge View search results to excel.');
        } else {
            const daySpan = moment(new Date(to)).diff(moment(new Date(from)), 'days');
            if (daySpan > 30) {
                this.setState({ showConfirmExportLargeDataset: true });
                return false;
            }
            this.exportToExcel();
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(event) {
        const rs = event.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
            selectedData: event.data
        });


        this.detailForm.current.open(event.data.id);
    }

    onDetailFormClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    getColumnDefs({ dispatchLocations, timesheetDetailStatuses, yesNo }) {
    // https://www.ag-grid.com/documentation/javascript/column-definitions/

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: this.indexCellRenderer,
            },
            {
                colId: 'JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                valueFormatter: function (params) {
                    return moment(params.value).format('yyyy-MM-DD');
                },
            },
            {
                colId: 'Customer',
                headerName: 'Customer',
                field: 'customer',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobName',
                headerName: 'Job Name',
                field: 'jobName',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'OrderNumber',
                headerName: 'Job / Order #',
                field: 'ordernumber',
                sortable: false,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'NumTrucks',
                headerName: '# Trucks',
                field: 'numTrucks',
                sortable: false,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
            },
            {
                colId: 'NumAssignedTrucks',
                headerName: '# Assigned Trucks',
                field: 'numAssignedTrucks',
                sortable: false,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
            }, {
                colId: 'NumAssignedDrivers',
                headerName: '# Assigned Drivers',
                field: 'numAssignedDrivers',
                sortable: false,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
            }
        ];

        return defs;
    }

    exportToExcel = async () => {
        await this.setState({ showConfirmExportLargeDataset: false, exporting: true });
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);
        const fileName = `JobDashboardSearch_${moment().format('YYYYMMDD_hhmmss')}.xlsx`;
        try {
            await util.fetch.downloadFile(ApiRoutes.jobDashboard.excelExport(), req, fileName);
        } catch (err) {
            toasty.error(`Error exporting data: ${err}`);
        } finally {
            await this.setState({ exporting: false });
        }
    };


    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const dispatchLocations = await util.fetch.js(ApiRoutes.typeAheads.dispatchLocations());
        const timesheetDetailStatuses = await util.fetch.js(ApiRoutes.typeAheads.timesheetStatuses());
        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const gridOptions = createGridOptions(this);

        gridOptions.onGridReady = (params) => {
            var begin = moment().isoWeekday(1).startOf('week');
            const start = begin.format(
                'YYYY-MM-DD',
            );
            const end = begin.add(6, 'days').format(
                'YYYY-MM-DD',
            );

            const filterInstance = gridOptions.api.getFilterInstance('JobDate');
            filterInstance.setModel({
                type: 'inRange',
                dateFrom: start,
                dateTo: end,
            });
            gridOptions.api.onFilterChanged();

            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
        };

        // API returns an object with counts as well as rowData for the grid.
        // Save counts to the state, and return the rowData to the grid.
        const context = this;

        gridOptions.postProcessData = (data) => {
            context.setState({
                rowData: data,
                scoreboardData: { ...data.counts } ?? null,
            });

            return { ...data.result };
        };
        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        // gridOptions.rowSelection = "multiple";

        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.columnDefs = this.getColumnDefs({ dispatchLocations, timesheetDetailStatuses, yesNo });
        gridOptions.masterDetail = true;

        const dataSource = createDataSource(ApiRoutes.jobDashboard.search(), gridOptions);

        this.setState({
            dispatchLocations,
            timesheetDetailStatuses,
            yesNo,
            loading: false,
            gridOptions,
            dataSource,
        });
    };

    render() {
        if (this.state.loading) return (<LinearProgress variant="indeterminate" color="secondary" />);

        const {
            exporting, rowData, gridOptions, scoreboardData
        } = this.state;


        var totalTrucks = 0;
        if (scoreboardData)
            totalTrucks = scoreboardData?.totalTrucks;

        var assignedTrucks = 0;
        if (scoreboardData)
            assignedTrucks = scoreboardData?.trucksAssigned;

        var assignedDrivers = 0;
        if (scoreboardData)
            assignedDrivers = scoreboardData?.driversAssigned;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon icon={faTasks} className="mr-2 text-muted" />
                    <span>Job Dashboard</span>
                </PageHeading>

                <FlexCenterRow
                    className="pb-3 pt-2"
                    id="timesheetWorkflowStatus"
                >
                    <span
                        style={{ minWidth: '100px' }}
                        className='mr-3 badge badge-lg badge-success p-2'
                    >
                        Required Trucks:
                        <span className="ml-2">{totalTrucks}</span>
                    </span>
                    <span
                        style={{ minWidth: '100px' }}
                        className='mr-3 badge badge-lg badge-success p-2'
                    >
                        Trucks Assigned:
                        <span className="ml-2">{assignedDrivers}</span>
                    </span>
                    <span
                        style={{ minWidth: '100px' }}
                        className='mr-3 badge badge-lg badge-success p-2'
                    >
                        Drivers Assigned:
                        <span className="ml-2">{assignedTrucks}</span>
                    </span>
                    
                </FlexCenterRow>

                <DataGridToolbar
                    entity="timechargeDetails"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                >

                    {/*<SmallButton*/}

                    {/*    disabled={!!this.state.saving}*/}
                    {/*    onClick={this.onExportToExcelClicked}*/}
                    {/*>*/}
                    {/*    <i className="fa fa-file-excel fa-lg" />*/}
                    {/*    <span className="ml-2 small-viewport-hide">Export to Excel</span>*/}
                    {/*</SmallButton>*/}

                </DataGridToolbar>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                    masterDetail={true}
                />
                
             
                <Modal backdrop="static" keyboard={false} isOpen={exporting}>
                    <ModalBody className="pt-5 pb-5">
                        <FlexCenterRow>
                            <span className="fa fa-lg fa-spin fa-circle-notch mr-2" />
                            <span>Exporting data, please wait...</span>
                        </FlexCenterRow>
                    </ModalBody>
                </Modal>

                <JobDashboardDetailForm
                    ref={this.detailForm}
                    onClose={this.onDetailFormClosed}
                    show={this.state.showDetailForm}
                    data={this.state.selectedData }
                />
            </PageWrap>
        );
    }
}

export default withRouter(JobDashboardIndex);
